import React, { useState, useEffect, useRef } from 'react';
import mapStyle from './mapStyle.json';
import { VendingMachineInfo } from '../../api/v1/api_pb';
import { Loader, LoaderOptions } from '@googlemaps/js-api-loader';

interface Props {
  vendingMachines: VendingMachineInfo[]
  selectedVendingMachine: VendingMachineInfo | null
  onSelectVendingMachine: Function;
  customCenter?: { lat: number; lng: number } | null;
  onMapReady?: () => void;
}

const defaultCenter = {
  lat: -33.87473252343564,
  lng: 151.203712449073
};


const API_KEY = "AIzaSyAiK6esuGcx32taWwFdMdQBGlNF0eNQkhs";

const defaultZoom = 12;


export default function VendingMachineMap(props: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const { selectedVendingMachine, vendingMachines, onSelectVendingMachine, customCenter, onMapReady } = props;

  // Calculate initial center and zoom ONCE on mount using lazy initialization
  // This ensures we always start with the correct center based on the selected machine
  const [initialCenter] = useState(() => {
    // Priority 1: Custom center from suburb search
    if (customCenter) {
      return customCenter;
    }

    // Priority 2: Selected vending machine (primary use case - clicking "Show on map")
    if (selectedVendingMachine?.latitude && selectedVendingMachine?.longitude) {
      return {
        lat: selectedVendingMachine.latitude,
        lng: selectedVendingMachine.longitude
      };
    }

    // Fallback: Use default Sydney center (should rarely happen)
    return defaultCenter;
  });

  const [initialZoom] = useState(() => {
    if (customCenter) return 13;
    if (selectedVendingMachine?.latitude) return 15;
    return defaultZoom;
  });

  // Store current center/zoom in state for dynamic updates
  const [center, setCenter] = useState(initialCenter);
  const [zoom, setZoom] = useState(initialZoom);

  // Update center/zoom when props change (for switching between machines)
  useEffect(() => {
    if (customCenter) {
      setCenter(customCenter);
      setZoom(13);
    } else if (selectedVendingMachine?.latitude && selectedVendingMachine?.longitude) {
      setCenter({
        lat: selectedVendingMachine.latitude,
        lng: selectedVendingMachine.longitude
      });
      setZoom(15);
    }
  }, [selectedVendingMachine, customCenter]);

  // Create Google Maps instance (only once on mount)
  // Using initialCenter/initialZoom directly - no complex ref logic needed
  useEffect(() => {
    const loaderOptions: LoaderOptions = {
      apiKey: API_KEY,
      version: 'weekly',
      nonce: 'base64value',
      libraries: ['places', 'marker']
    };

    const loader = new Loader(loaderOptions);

    loader.load().then(() => {
      if (mapRef.current) {
        // Check if AdvancedMarkerElement is available
        const useAdvancedMarkers = !!(google.maps.marker && google.maps.marker.AdvancedMarkerElement);

        const mapOptions: google.maps.MapOptions = {
          center: initialCenter,
          zoom: initialZoom,
          // Disable the default UI to prevent Google's search box from appearing
          // But keep essential controls like zoom
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false
        };

        // Only add mapId if using AdvancedMarkerElement, otherwise add styles
        if (useAdvancedMarkers) {
          mapOptions.mapId = 'vending_machine_map';
        } else {
          mapOptions.styles = mapStyle;
        }

        const mapInstance = new google.maps.Map(mapRef.current, mapOptions);
        setMap(mapInstance);

        // Listen for bounds_changed which fires as soon as the map viewport is set
        // This happens much faster than 'idle' or 'tilesloaded'
        const boundsListener = mapInstance.addListener('bounds_changed', () => {
          if (!mapLoaded) {
            setMapLoaded(true);
            // Use requestAnimationFrame to ensure the DOM has been painted
            requestAnimationFrame(() => {
              if (onMapReady) {
                onMapReady();
              }
            });
            // Remove the listener after first call
            google.maps.event.removeListener(boundsListener);
          }
        });
      }
    }).catch(error => {
      console.error('Error loading Google Maps:', error);
    });
  }, [mapLoaded, onMapReady, initialCenter, initialZoom]);

  // Update map center when center or zoom changes
  useEffect(() => {
    if (map) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  useEffect(() => {
    if (map) {
      // Clear existing markers before creating new ones
      const markers: any[] = [];

      vendingMachines.forEach((vendingMachine, index) => {
        // Check if AdvancedMarkerElement is available
        if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
          // Create a container for the marker content
          const content = document.createElement('div');
          content.style.position = 'relative';
          content.style.width = '40px';
          content.style.height = '40px';

          // Create the circle background
          const circle = document.createElement('div');
          circle.style.width = '40px';
          circle.style.height = '40px';
          circle.style.borderRadius = '50%';
          circle.style.backgroundColor = selectedVendingMachine === vendingMachine ? '#6838d3' : '#000000';
          circle.style.border = '2px solid #ffffff';
          circle.style.display = 'flex';
          circle.style.alignItems = 'center';
          circle.style.justifyContent = 'center';
          circle.style.cursor = 'pointer';

          // Create the label
          const label = document.createElement('div');
          label.textContent = `${index + 1}`;
          label.style.color = '#ffffff';
          label.style.fontSize = '18px';
          label.style.fontWeight = 'bold';

          circle.appendChild(label);
          content.appendChild(circle);

          // Create the AdvancedMarkerElement
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position: { lat: vendingMachine.latitude ?? 0, lng: vendingMachine.longitude ?? 0 },
            map: map,
            title: vendingMachine.address ?? '-',
            content: content
          });

          // Add click listener
          marker.addListener('click', () => {
            onSelectVendingMachine(vendingMachine);
          });

          markers.push(marker);
        } else {
          // Fallback to regular Marker if AdvancedMarkerElement is not available
          const marker = new google.maps.Marker({
            position: { lat: vendingMachine.latitude ?? 0, lng: vendingMachine.longitude ?? 0 },
            map: map,
            title: vendingMachine.address ?? '-',
            label: {
              text: `${index + 1}`,
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 'bold',
            },
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: selectedVendingMachine === vendingMachine ? '#6838d3' : '#000000',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 20,
            },
          });

          marker.addListener('click', () => {
            onSelectVendingMachine(vendingMachine);
          });

          markers.push(marker);
        }
      });

      // Cleanup function to remove markers when component unmounts or dependencies change
      return () => {
        markers.forEach(marker => {
          marker.map = null;
        });
      };
    }
  }, [map, vendingMachines, selectedVendingMachine, onSelectVendingMachine]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}